<!--
  Add/edit one bookable resource (a doctor, a room, a specific staff member)
  -- rendered INLINE in the Slot booking tab (below "Add resource", or in
  place of the row being edited), not as a popup dialog. Ramu's own
  feedback (2026-09-04): "everything in one single navigation... this way
  no one works." The parent (CampaignSlotBooking.vue) mounts/unmounts this
  with v-if, so a fresh instance is created each time it opens -- no
  open/close watcher needed, initial state just reads straight off props.

  Each day is a set of time-picker windows (start/end), not a raw text
  field -- and every day shows a LIVE preview of the actual slots that
  configuration produces (e.g. "10:00-11:15 · 11:30-12:45"), computed with
  the exact same minutes-of-day arithmetic the backend uses
  (generateDaySlotStarts in index.mjs) so what you see here is what the AI
  will actually offer callers. Several windows per day = a break in the
  middle (e.g. lunch): add another window instead of typing a comma.
-->
<template>
  <div class="srp-panel">
    <div class="srp-panel__head">
      <div class="srp-panel__icon">
        <v-icon icon="mdi-calendar-account-outline" size="18" />
      </div>
      <div class="srp-panel__title">{{ isEdit ? 'Edit resource' : 'Add resource' }}</div>
      <v-spacer />
      <v-btn icon="mdi-close" variant="text" density="comfortable" size="small" @click="cancel" />
    </div>

    <p class="srp-panel__desc">
      Slots are offered only inside the hours you set below, per day of the week -- add
      a window per day, or a few if there's a break in the middle. Leave a day with no
      windows to stay closed that day.
    </p>

    <label class="srp-field">
      <span class="srp-label">Name<span class="srp-required">*</span></span>
      <v-text-field
        v-model="form.resource_name"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        placeholder="e.g. Dr. Sharma, or Room 2"
        maxlength="120"
      />
    </label>

    <div class="srp-row">
      <label class="srp-field">
        <span class="srp-label">Slot length (minutes)</span>
        <v-text-field
          v-model.number="form.slot_duration_minutes"
          type="number"
          min="5"
          step="5"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />
        <p class="srp-hint">How long each booking lasts. This is what slices the hours below into the slots shown in each day's preview.</p>
      </label>
      <label class="srp-field">
        <span class="srp-label">Capacity per slot</span>
        <v-text-field
          v-model.number="form.max_concurrent"
          type="number"
          min="1"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />
        <p class="srp-hint">How many callers can be booked into the very same slot -- 1 for a single doctor or room, more for something shared.</p>
      </label>
    </div>

    <div class="srp-channel-head">
      <span>Active</span>
      <v-switch v-model="form.is_active" color="primary" density="compact" hide-details />
    </div>
    <p class="srp-hint" style="margin-top: -4px; margin-bottom: 16px">
      Inactive resources are never offered, but keep their existing bookings.
    </p>

    <div class="srp-section-title">Hours &amp; slots</div>

    <div v-for="day in WEEKDAYS" :key="day.key" class="srp-day">
      <div class="srp-day__head">
        <span class="srp-day__label">{{ day.label }}</span>
        <span class="srp-day__badge" :class="{ 'srp-day__badge--open': dayWindows[day.key].length }">
          {{ dayWindows[day.key].length ? `${daySlotCount(day.key)} slot${daySlotCount(day.key) === 1 ? '' : 's'}` : 'Closed' }}
        </span>
      </div>

      <div v-for="(w, idx) in dayWindows[day.key]" :key="idx" class="srp-window-row">
        <input v-model="w.start" type="time" class="srp-time-input" />
        <span class="srp-window-sep">to</span>
        <input v-model="w.end" type="time" class="srp-time-input" />
        <input
          v-model.number="w.duration"
          type="number"
          min="5"
          step="5"
          class="srp-window-duration"
          :placeholder="String(form.slot_duration_minutes || 30)"
          title="Override this window's own slot length (minutes) -- leave blank to use this resource's slot length above"
        />
        <span class="srp-window-duration-suffix">min</span>
        <button type="button" class="srp-window-remove" aria-label="Remove this window" @click="removeWindow(day.key, idx)">
          <v-icon icon="mdi-close" size="15" />
        </button>
      </div>

      <button type="button" class="srp-add-window" @click="addWindow(day.key)">
        <v-icon icon="mdi-plus" size="15" />
        {{ dayWindows[day.key].length ? 'Add another window' : 'Add hours' }}
      </button>

      <p v-if="windowError(day.key)" class="srp-error">{{ windowError(day.key) }}</p>
      <div v-else-if="dayWindows[day.key].length" class="srp-day__preview">
        <div v-if="daySlots(day.key).length" class="srp-chip-row">
          <span
            v-for="(s, i) in daySlotsShown(day.key)"
            :key="i"
            class="srp-chip"
            :class="`srp-chip--c${i % SLOT_CHIP_COLOR_COUNT}`"
          >
            {{ s.start }}–{{ s.end }}
          </span>
          <button
            v-if="daySlotOverflowCount(day.key) > 0"
            type="button"
            class="srp-chip srp-chip--more"
            @click="toggleExpanded(day.key)"
          >
            +{{ daySlotOverflowCount(day.key) }} more
          </button>
          <button
            v-else-if="expandedDays[day.key] && daySlots(day.key).length > SLOT_PREVIEW_COLLAPSED_COUNT"
            type="button"
            class="srp-chip srp-chip--more"
            @click="toggleExpanded(day.key)"
          >
            Show less
          </button>
        </div>
        <span v-else>No slots fit these hours at the current slot length -- try shortening it or widening the window.</span>
      </div>
    </div>

    <div class="srp-actions">
      <v-btn variant="text" @click="cancel">Cancel</v-btn>
      <v-btn variant="flat" color="primary" :loading="saving" @click="save">
        {{ isEdit ? 'Save' : 'Add resource' }}
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { createSlotResource, updateSlotResource } from '@/api/campaignsApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'SlotResourceForm' })

const props = defineProps({
  taskId: { type: String, default: '' },
  /** null = create mode; an existing resource row = edit mode. */
  resource: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'cancel', 'error'])

// Sun-first, matching the backend's SLOT_WEEKDAY_HOURS_FIELD/hours_<dow> keys.
const WEEKDAYS = [
  { key: 'sun', label: 'Sunday' },
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
]

// Only used to PARSE an already-saved "HH:MM-HH:MM[:MINUTES],..." string
// back into editable {start,end,duration} windows -- typing that format is
// no longer how a person enters hours (that's what the time pickers and the
// per-window minutes field are for), but it's still exactly what gets
// serialized back out on save, so existing resources round-trip unchanged.
// The trailing ":MINUTES" is an optional per-window slot-length override
// (2026-09-05, Ramu: different windows on the same day can reasonably want
// different appointment lengths) -- duration: null means "use this
// resource's own slot length" above, matching the backend's own default.
const HOURS_REGEX = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)(?::(\d{1,3}))?$/

function parseHoursString(hoursStr) {
  const value = String(hoursStr || '').trim()
  if (!value) return []
  return value
    .split(',')
    .map((w) => w.trim())
    .filter(Boolean)
    .map((w) => {
      const m = HOURS_REGEX.exec(w)
      if (!m) return null
      return { start: `${m[1]}:${m[2]}`, end: `${m[3]}:${m[4]}`, duration: m[5] ? Number(m[5]) : null }
    })
    .filter(Boolean)
}

// How many of a day's slots show before collapsing into "+N more" -- a
// plain constant instead of a magic 6 sprinkled through the template/script,
// and used in the "Show less" condition below so the two stay in sync.
const SLOT_PREVIEW_COLLAPSED_COUNT = 6
// Cycling chip colors (see .srp-chip--c0..c5 below) -- purely decorative
// variety across a day's slots, not a categorical encoding of anything, so
// cycling by plain index is fine (no meaning is being color-coded here).
const SLOT_CHIP_COLOR_COUNT = 6

const isEdit = computed(() => Boolean(props.resource?.resource_id))

// A fresh component instance is created each time this panel opens (the
// parent toggles it with v-if), so reading props once here at setup time
// is exactly the right moment to seed state -- no open/close watcher needed.
const form = ref(
  props.resource
    ? {
        resource_name: String(props.resource.resource_name ?? '').trim(),
        slot_duration_minutes: Number(props.resource.slot_duration_minutes) || 30,
        max_concurrent: Number(props.resource.max_concurrent) || 1,
        is_active: props.resource.is_active !== false,
      }
    : { resource_name: '', slot_duration_minutes: 30, max_concurrent: 1, is_active: true },
)
const saving = ref(false)
const dayWindows = reactive(
  Object.fromEntries(WEEKDAYS.map((d) => [d.key, parseHoursString(props.resource?.[`hours_${d.key}`])])),
)

function addWindow(dow) {
  dayWindows[dow].push({ start: '', end: '', duration: null })
}
function removeWindow(dow, idx) {
  dayWindows[dow].splice(idx, 1)
}

function windowError(dow) {
  for (const w of dayWindows[dow]) {
    if (!w.start || !w.end) return 'Set both a start and an end time.'
    if (w.end <= w.start) return 'End time must be after the start time.'
  }
  return ''
}

const firstWindowError = computed(() => {
  for (const day of WEEKDAYS) {
    const message = windowError(day.key)
    if (message) return `${day.label}: ${message}`
  }
  return ''
})

// Mirrors the backend's generateDaySlotStarts exactly: walk each window in
// whole-duration steps, a slot only counts if it fits before the window
// closes. Plain minutes-of-day arithmetic -- no timezone conversion needed,
// this is describing a civil-time schedule, not converting a moment.
function toMinutes(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
function minutesToLabel(mins) {
  const h = String(Math.floor(mins / 60)).padStart(2, '0')
  const m = String(mins % 60).padStart(2, '0')
  return `${h}:${m}`
}
function computeDaySlots(windows, defaultDurationMinutes) {
  const defaultDuration = Number(defaultDurationMinutes) > 0 ? Number(defaultDurationMinutes) : 30
  const slots = []
  for (const w of windows) {
    if (!w.start || !w.end) continue
    const open = toMinutes(w.start)
    const close = toMinutes(w.end)
    if (close <= open) continue
    // A window's own override wins over the resource's default -- mirrors
    // generateDaySlotStarts's own m[5] handling in index.mjs exactly.
    const duration = Number(w.duration) > 0 ? Number(w.duration) : defaultDuration
    for (let mins = open; mins + duration <= close; mins += duration) {
      slots.push({ start: minutesToLabel(mins), end: minutesToLabel(mins + duration) })
    }
  }
  return slots
}

function daySlots(dow) {
  return computeDaySlots(dayWindows[dow], form.value.slot_duration_minutes)
}
function daySlotCount(dow) {
  return daySlots(dow).length
}

// "+N more" is a real toggle, not just inert truncated text (2026-09-05,
// Ramu: "if I click on forty seven... plus forty one more? It doesn't
// come" -- there was no click handler at all before this).
const expandedDays = reactive(Object.fromEntries(WEEKDAYS.map((d) => [d.key, false])))
function toggleExpanded(dow) {
  expandedDays[dow] = !expandedDays[dow]
}
function daySlotsShown(dow) {
  const slots = daySlots(dow)
  return expandedDays[dow] ? slots : slots.slice(0, SLOT_PREVIEW_COLLAPSED_COUNT)
}
function daySlotOverflowCount(dow) {
  if (expandedDays[dow]) return 0
  return Math.max(0, daySlots(dow).length - SLOT_PREVIEW_COLLAPSED_COUNT)
}

function cancel() {
  emit('cancel')
}

async function save() {
  const resourceName = String(form.value.resource_name ?? '').trim()
  if (!resourceName) {
    emit('error', 'Resource name is required.')
    return
  }
  if (firstWindowError.value) {
    emit('error', firstWindowError.value)
    return
  }

  saving.value = true
  try {
    const payload = {
      resource_name: resourceName,
      slot_duration_minutes: Number(form.value.slot_duration_minutes) || 30,
      max_concurrent: Number(form.value.max_concurrent) || 1,
      is_active: form.value.is_active,
    }
    for (const day of WEEKDAYS) {
      payload[`hours_${day.key}`] = dayWindows[day.key]
        .filter((w) => w.start && w.end)
        .map((w) => `${w.start}-${w.end}${Number(w.duration) > 0 ? `:${Number(w.duration)}` : ''}`)
        .join(',')
    }

    const response = isEdit.value
      ? await updateSlotResource({ resource_id: props.resource.resource_id, ...payload })
      : await createSlotResource({ task_id: props.taskId, ...payload })

    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save resource'))
      return
    }
    emit('saved', isEdit.value ? 'Resource updated.' : 'Resource added.')
  } catch (err) {
    emit('error', err)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/*
  Theme-aware throughout -- every color below reads from the portal's own
  Vuetify theme tokens (--y-*, --v-theme-*) instead of hardcoded hex, so
  this panel matches whichever theme is active instead of always looking
  like a light-mode card dropped into a dark app (the real bug behind
  "the slot UI looks bad": every color here used to be hardcoded for
  light backgrounds only, so it looked broken/mismatched whenever dark
  theme was active -- which used to be the portal's default).
*/
.srp-panel {
  border: 1px solid rgba(var(--v-theme-primary), 0.28);
  border-radius: 12px;
  padding: 14px 16px;
  margin: 10px 0;
  background: rgba(var(--v-theme-primary), 0.04);
}
.srp-panel__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.srp-panel__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}
.srp-panel__title {
  font-weight: 650;
  font-size: 0.95rem;
  color: var(--y-text);
}
.srp-panel__desc {
  font-size: 0.82rem;
  color: var(--y-text-secondary);
  margin: 0 0 16px;
}
.srp-field {
  display: block;
  margin-bottom: 14px;
}
.srp-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--y-text);
}
.srp-required {
  color: rgb(var(--v-theme-error));
  margin-left: 2px;
}
.srp-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.srp-hint {
  font-size: 0.78rem;
  color: var(--y-text-muted);
  margin: 4px 0 0;
}
.srp-error {
  font-size: 0.78rem;
  color: rgb(var(--v-theme-error));
  font-weight: 600;
  margin: 6px 0 0;
}
.srp-section-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--y-text-muted);
  margin-bottom: 10px;
}
.srp-channel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--y-text);
}

.srp-day {
  border: 1px solid var(--y-border);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: var(--y-surface);
}
.srp-day__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.srp-day__label {
  font-size: 0.85rem;
  font-weight: 650;
  color: var(--y-text);
}
.srp-day__badge {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--y-text-muted);
  background: var(--y-surface-hover);
  border-radius: 999px;
  padding: 2px 9px;
}
.srp-day__badge--open {
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.14);
}
.srp-window-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.srp-time-input {
  border: 1px solid var(--y-field-border);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-family: inherit;
  color: var(--y-field-text);
  background: var(--y-field-bg);
  line-height: 1.2;
  color-scheme: light dark;
}
.srp-time-input:focus {
  outline: none;
  border-color: var(--y-field-focus-border);
  box-shadow: var(--y-field-focus-ring);
}
.srp-window-sep {
  font-size: 0.78rem;
  color: var(--y-text-muted);
}
.srp-window-duration {
  width: 56px;
  border: 1px solid var(--y-field-border);
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 0.85rem;
  font-family: inherit;
  color: var(--y-field-text);
  background: var(--y-field-bg);
  line-height: 1.2;
  margin-left: 4px;
}
.srp-window-duration:focus {
  outline: none;
  border-color: var(--y-field-focus-border);
  box-shadow: var(--y-field-focus-ring);
}
.srp-window-duration-suffix {
  font-size: 0.76rem;
  color: var(--y-text-muted);
}
.srp-window-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--y-text-muted);
  cursor: pointer;
  margin-left: auto;
}
.srp-window-remove:hover {
  background: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-error));
}
.srp-add-window {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: rgb(var(--v-theme-primary));
  font-size: 0.78rem;
  font-weight: 650;
  cursor: pointer;
  padding: 2px 0;
  margin-top: 2px;
}
.srp-add-window:hover {
  text-decoration: underline;
}
.srp-day__preview {
  font-size: 0.76rem;
  color: var(--y-text-muted);
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px dashed var(--y-border);
}
.srp-chip-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.srp-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 650;
  font-family: inherit;
  line-height: 1.4;
  white-space: nowrap;
  border: none;
}
/* Purely decorative variety across a day's open slots -- not a categorical
   encoding (every chip means the same thing: "a bookable slot"), so cycling
   these by plain index is fine. Each has its own light/dark pair, same
   "design both themes explicitly" approach as the rest of this file --
   these hues have no existing --y-* / --v-theme-* token, so they're the one
   place in this component with hardcoded color, deliberately paired per
   theme via the .v-theme--dark override below rather than left light-only. */
.srp-chip--c0 { background: rgba(99, 102, 241, 0.14); color: #4338ca; }
.srp-chip--c1 { background: rgba(20, 184, 166, 0.14); color: #0f766e; }
.srp-chip--c2 { background: rgba(245, 158, 11, 0.16); color: #b45309; }
.srp-chip--c3 { background: rgba(244, 63, 94, 0.14); color: #be123c; }
.srp-chip--c4 { background: rgba(168, 85, 247, 0.14); color: #7e22ce; }
.srp-chip--c5 { background: rgba(14, 165, 233, 0.14); color: #0369a1; }
.v-theme--dark .srp-chip--c0 { background: rgba(129, 140, 248, 0.2); color: #a5b4fc; }
.v-theme--dark .srp-chip--c1 { background: rgba(45, 212, 191, 0.2); color: #5eead4; }
.v-theme--dark .srp-chip--c2 { background: rgba(251, 191, 36, 0.2); color: #fcd34d; }
.v-theme--dark .srp-chip--c3 { background: rgba(251, 113, 133, 0.2); color: #fda4af; }
.v-theme--dark .srp-chip--c4 { background: rgba(196, 132, 252, 0.2); color: #d8b4fe; }
.v-theme--dark .srp-chip--c5 { background: rgba(56, 189, 248, 0.2); color: #7dd3fc; }
.srp-chip--more {
  background: var(--y-surface-hover);
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}
.srp-chip--more:hover {
  background: rgba(var(--v-theme-primary), 0.16);
}
.srp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--y-border);
}

@media (max-width: 500px) {
  .srp-row {
    grid-template-columns: 1fr;
  }
}
</style>
